import { http, HttpResponse, delay } from 'msw';
import { ApplicationSlug } from '@/constants/applications';
import { notionResourcesBySettingName } from '@/mocks/data/requestTypeSettingsData';
import {
  markApplicationMockConnected,
  markApplicationMockDisconnected,
} from '@/mocks/data/mockConnectedIntegrations';
import {
  createOAuthConfigurationResponse,
  healthyIntegrationResponse,
  nangoConnectionTokenResponse,
  supportedProfileFields,
  supportedProfileFieldsByApplication,
} from '@/mocks/data/integrationData';

/**
 * Mocks for endpoints introduced by the Google Workspace license-assignment
 * work (FD-3369). Kept small on purpose — add additional variants (404 for
 * disconnected integrations, empty missing_scopes, etc.) as tests grow.
 */

// Shared by every mock OAuth callback endpoint below -- decodes the
// application key IntegrationsConnect.tsx encoded into `state`
// (get-authorization-url handler further down) and marks it connected.
const markApplicationMockConnectedFromState = (requestUrl: string) => {
  const state = new URL(requestUrl).searchParams.get('state') ?? '';
  try {
    const applicationKey = JSON.parse(
      atob(state.split('.')[1] ?? '')
    ).application_key;
    if (applicationKey) {
      markApplicationMockConnected(applicationKey);
    }
  } catch {
    // Malformed/missing state -- nothing to mark connected.
  }
};

const googleProductListings = [
  {
    composite_id: 'Google Workspace:Google-Apps:1010020027',
    product_family: 'Google Workspace',
    product_id: 'Google-Apps',
    product_name: 'Google Workspace Business',
    sku_id: '1010020027',
    sku_name: 'Business Starter',
    is_additional_license: false,
  },
  {
    composite_id: 'Google Workspace:Google-Apps:1010020028',
    product_family: 'Google Workspace',
    product_id: 'Google-Apps',
    product_name: 'Google Workspace Business',
    sku_id: '1010020028',
    sku_name: 'Business Standard',
    is_additional_license: false,
  },
  {
    composite_id: 'Google Workspace:Google-Apps:1010020029',
    product_family: 'Google Workspace',
    product_id: 'Google-Apps',
    product_name: 'Google Workspace Business',
    sku_id: '1010020029',
    sku_name: 'Business Plus',
    is_additional_license: false,
  },
  {
    composite_id: 'Google Workspace:Google-Vault:Google-Vault-Former-Employee',
    product_family: 'Google Workspace',
    product_id: 'Google-Vault',
    product_name: 'Google Vault',
    sku_id: 'Google-Vault-Former-Employee',
    sku_name: 'Vault Former Employee',
    is_additional_license: true,
  },
  {
    composite_id: 'Google Workspace:Google-Vault:Google-Vault',
    product_family: 'Google Workspace',
    product_id: 'Google-Vault',
    product_name: 'Google Vault',
    sku_id: 'Google-Vault',
    sku_name: 'Vault',
    is_additional_license: true,
  },
];

// Light in-memory OAuth configuration store (keyed by org + application) so the
// create -> get -> update -> delete cycle in the OAuth settings UI is coherent.
const oauthConfigurations = new Map<string, string>();
const oauthConfigurationKey = (
  organizationId: string,
  applicationKey: string
) => `${organizationId}:${applicationKey}`;

export const integrationHandlers = [
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/token-scopes',
    async ({ params }) => {
      await delay();
      const { applicationKey } = params;
      if (applicationKey !== ApplicationSlug.GoogleWorkspace) {
        return HttpResponse.json(
          {
            configured_scopes: [],
            granted_scopes: [],
            missing_scopes: [],
          },
          { status: 200 }
        );
      }
      // Simulate a recently-expanded scope list so the banner shows in dev.
      return HttpResponse.json(
        {
          configured_scopes: [
            'https://www.googleapis.com/auth/admin.directory.user',
            'https://www.googleapis.com/auth/apps.licensing',
          ],
          granted_scopes: [
            'https://www.googleapis.com/auth/admin.directory.user',
          ],
          missing_scopes: ['https://www.googleapis.com/auth/apps.licensing'],
        },
        { status: 200 }
      );
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/resource/license_sku_ids',
    async () => {
      await delay();
      return HttpResponse.json(
        {
          application_id: 'google-workspace',
          resource_label: 'sku_name',
          resource_value: 'composite_id',
          resource: googleProductListings,
        },
        { status: 200 }
      );
    }
  ),
  // Powers the option lists for Notion's mocked provisioning settings in the
  // Configure Applications flow's expandable rows (see requestTypeSettingsData.ts).
  http.get(
    '*/v1/organizations/:organizationId/applications/notion/integration/resource/:resourceSlug',
    async ({ params }) => {
      await delay();
      const { resourceSlug } = params;
      const resource =
        notionResourcesBySettingName[resourceSlug as string] ?? [];
      return HttpResponse.json(
        { application_id: 'notion', resource },
        { status: 200 }
      );
    }
  ),
  // Fallback for any other integration resource lookup (empty option list).
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/resource/:resourceSlug',
    async ({ params }) => {
      await delay();
      return HttpResponse.json(
        {
          result: 'success',
          application_id: params.applicationKey,
          resource_label: 'name',
          resource_value: 'id',
          resource: [],
        },
        { status: 200 }
      );
    }
  ),
  // Used by useHealthCheckQuery and useBatchHealthCheckQuery.
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/health',
    async () => {
      await delay();
      return HttpResponse.json(healthyIntegrationResponse, { status: 200 });
    }
  ),
  http.delete(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration',
    async ({ params }) => {
      markApplicationMockDisconnected(params.applicationKey as string);
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/supported-profile-fields',
    async ({ params }) => {
      await delay();
      const applicationKey = String(params.applicationKey);
      return HttpResponse.json(
        {
          fields: Object.hasOwn(
            supportedProfileFieldsByApplication,
            applicationKey
          )
            ? supportedProfileFieldsByApplication[applicationKey]
            : supportedProfileFields,
        },
        { status: 200 }
      );
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/nango/connection-token',
    async () => {
      await delay();
      return HttpResponse.json(nangoConnectionTokenResponse, { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/nango-connection',
    async ({ params, request }) => {
      const { organizationId, applicationKey } = params;
      const body = (await request.json()) as { connection_id: string };
      markApplicationMockConnected(applicationKey as string);
      await delay();
      return HttpResponse.json(
        {
          organization_id: organizationId,
          application_id: applicationKey,
          connection_id: body.connection_id,
        },
        { status: 200 }
      );
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/applications/:applicationKey/oauth/token',
    async ({ params }) => {
      markApplicationMockConnected(params.applicationKey as string);
      await delay();
      return HttpResponse.json(healthyIntegrationResponse, { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/applications/:applicationKey/oauth/configuration',
    async ({ params }) => {
      const { organizationId, applicationKey } = params as Record<
        string,
        string
      >;
      const configurationId = `${applicationKey}-org-application`;
      oauthConfigurations.set(
        oauthConfigurationKey(organizationId, applicationKey),
        configurationId
      );
      await delay();
      return HttpResponse.json(
        createOAuthConfigurationResponse(configurationId),
        { status: 201 }
      );
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/oauth/configuration',
    async ({ params }) => {
      const { organizationId, applicationKey } = params as Record<
        string,
        string
      >;
      const configurationId = oauthConfigurations.get(
        oauthConfigurationKey(organizationId, applicationKey)
      );
      await delay();
      if (!configurationId) {
        return new HttpResponse(null, { status: 404 });
      }
      return HttpResponse.json(
        createOAuthConfigurationResponse(configurationId),
        { status: 200 }
      );
    }
  ),
  http.put(
    '*/v1/organizations/:organizationId/applications/:applicationKey/oauth/configuration',
    async ({ params }) => {
      const { organizationId, applicationKey } = params as Record<
        string,
        string
      >;
      const configurationId =
        oauthConfigurations.get(
          oauthConfigurationKey(organizationId, applicationKey)
        ) ?? `${applicationKey}-org-application`;
      await delay();
      return HttpResponse.json(
        createOAuthConfigurationResponse(configurationId),
        { status: 200 }
      );
    }
  ),
  http.delete(
    '*/v1/organizations/:organizationId/applications/:applicationKey/oauth/configuration',
    async ({ params }) => {
      const { organizationId, applicationKey } = params as Record<
        string,
        string
      >;
      oauthConfigurations.delete(
        oauthConfigurationKey(organizationId, applicationKey)
      );
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/integrations/usage',
    async () => {
      await delay();
      return HttpResponse.json(
        {
          connected_count: 2,
          applications_limit: 10,
        },
        { status: 200 }
      );
    }
  ),
  // Powers the Configure step's "Connect" button
  // (useLazyGetIntegrationSupportQuery) — always OAuth in mocks, since Nango
  // requires a real Nango public key/backend to do anything useful locally.
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/integration/support',
    async ({ params }) => {
      await delay();
      // Profile Sync only renders when `update_profile` is supported (TF-892).
      const supportsUpdateProfile = Object.hasOwn(
        supportedProfileFieldsByApplication,
        String(params.applicationKey)
      );
      return HttpResponse.json(
        {
          type: 'OAUTH',
          operations: supportsUpdateProfile ? ['update_profile'] : [],
        },
        { status: 200 }
      );
    }
  ),
  // Powers the rest of the Connect flow after "Connect" is clicked in the
  // Configure step: IntegrationsConnect.tsx calls this to get somewhere to
  // send the popup window. The real backend returns a third-party OAuth
  // URL, which mocks can't do anything useful with -- instead this points
  // the popup at our own callback route (same origin, so it works without a
  // real provider), round-tripping the application key through `state` the
  // same way IntegrationsCallback.tsx expects to decode it
  // (`JSON.parse(atob(state.split('.')[1]))`).
  //
  // IntegrationsCallback.tsx branches to a different callback query
  // depending on the application (GitHub uses installation_id, Microsoft
  // 365/Entra ID/Intune use tenant, everything else uses code) -- rather
  // than branch here too, every possible param is included so whichever
  // query the callback page actually runs finds what it needs.
  http.get(
    '*/v1/integrations/oauth/get-authorization-url',
    async ({ request }) => {
      const url = new URL(request.url);
      const applicationKey = url.searchParams.get('application_key') ?? '';
      const state = `mock.${btoa(
        JSON.stringify({ application_key: applicationKey })
      )}.mock`;
      const callbackParams = new URLSearchParams({
        code: 'mock-code',
        installation_id: 'mock-installation-id',
        tenant: 'mock-tenant',
        state,
      });
      const callbackUrl = `/integrations/oauth/callback?${callbackParams.toString()}`;

      await delay();
      return HttpResponse.json(
        { authorization_url: callbackUrl },
        { status: 200 }
      );
    }
  ),
  // Standard OAuth callback -- landed on by the popup for every application
  // except GitHub/Microsoft 365/Entra ID/Intune, which use their own
  // provider-specific callback endpoints below. IntegrationsCallback.tsx
  // treats "not loading, no error" as success, so this just needs to
  // resolve -- but the actual "connected" state the Configure table cares
  // about lives on the org application record, so this also marks that
  // application connected before responding, the same way a real
  // successful OAuth callback would server-side. See
  // mockConnectedIntegrations.ts for why that's persisted via localStorage
  // rather than mutating organizationApplicationsList directly -- this
  // handler runs in the popup's own separate window/MSW instance, not the
  // opener's, so an in-memory mutation here wouldn't be visible there.
  http.get('*/v1/integrations/oauth/callback', async ({ request }) => {
    markApplicationMockConnectedFromState(request.url);
    await delay();
    return HttpResponse.json({ result: 'success' }, { status: 200 });
  }),
  // GitHub App installation callback (useGetGitHubAppCallbackQuery).
  http.get('*/v1/integrations/oauth/github-callback', async ({ request }) => {
    markApplicationMockConnectedFromState(request.url);
    await delay();
    return HttpResponse.json({ result: 'success' }, { status: 200 });
  }),
  // Microsoft 365 / Entra ID callback (useGetMicrosoftOAuthCallbackQuery).
  // Response type is `void` on the real endpoint -- an empty 200 is enough.
  http.get(
    '*/v1/integrations/oauth/microsoft-callback',
    async ({ request }) => {
      markApplicationMockConnectedFromState(request.url);
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  // Microsoft Intune callback (useGetMicrosoftIntuneOAuthCallbackQuery).
  // Same response shape as the Microsoft 365 callback above.
  http.get(
    '*/v1/integrations/oauth/microsoft-intune/callback',
    async ({ request }) => {
      markApplicationMockConnectedFromState(request.url);
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  // Microsoft on-prem Active Directory callback
  // (useGetMicrosoftActiveDirectoryOAuthCallbackQuery). Same shape as above.
  http.get(
    '*/v1/integrations/oauth/microsoft-on-prem-active-directory/callback',
    async ({ request }) => {
      markApplicationMockConnectedFromState(request.url);
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
];
